/**
 * Options for canvas to image conversion
 */
export interface CanvasToImageOptions {
  /** Image format (e.g., 'image/jpeg', 'image/png'). Default: 'image/jpeg' */
  format?: string;
  /** Quality for lossy formats like JPEG (0-1). Default: 0.9 */
  quality?: number;
}

/**
 * Converts a canvas element to an object URL using toBlob for better performance
 *
 * This approach is more efficient than toDataURL() especially for large images:
 * - Asynchronous (doesn't block the main thread)
 * - More memory efficient (creates a Blob, not a large Base64 string)
 * - Supports quality control via compression
 *
 * @param canvas - The canvas element to convert
 * @param options - Configuration options for the conversion
 * @returns Promise that resolves to an object URL string
 * @throws Error if canvas context is invalid or blob creation fails
 */
export const canvasToImageUrl = async (
  canvas: HTMLCanvasElement,
  options: CanvasToImageOptions = {}
): Promise<string> => {
  const {
    format = 'image/jpeg',
    quality = 0.9
  } = options;

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }
        try {
          const objectUrl = URL.createObjectURL(blob);
          resolve(objectUrl);
        } catch (error) {
          reject(error);
        }
      },
      format,
      quality
    );
  });
};

/**
 * Revokes an object URL created by canvasToImageUrl to free up memory
 *
 * Always call this when you're done with the URL to prevent memory leaks
 *
 * @param objectUrl - The object URL to revoke
 */
export const revokeImageUrl = (objectUrl: string): void => {
  if (objectUrl && objectUrl.startsWith('blob:')) {
    URL.revokeObjectURL(objectUrl);
  }
};
